import api from '../api/api';

// Discount rules of the logged-in user, kept in memory and in localStorage so every tab of
// the same browser shares them and the rules are fetched once instead of once per screen.
export const DISCOUNT_RULES_STORAGE_KEY = 'discountRules:v2';
const OLD_SESSION_KEY = 'discountRules:v1';
const MAX_AGE_MS = 30 * 60 * 1000;

let memory = null; // { userId, rules, loadedAt }
let inFlight = null; // shared promise while one GET is running

function isFresh(entry, userId) {
  if (!entry || entry.userId !== userId || !Array.isArray(entry.rules)) {
    return false;
  }
  return Date.now() - entry.loadedAt <= MAX_AGE_MS;
}

function readStorage() {
  try {
    const text = localStorage.getItem(DISCOUNT_RULES_STORAGE_KEY);
    if (!text) {
      return null;
    }
    return JSON.parse(text);
  } catch (err) {
    return null;
  }
}

function writeStorage(entry) {
  try {
    localStorage.setItem(DISCOUNT_RULES_STORAGE_KEY, JSON.stringify(entry));
  } catch (err) {
    // Storage blocked or full: the memory copy still works.
  }
}

// Returns the rules array, or null when nothing usable is cached.
// An empty array counts as loaded, so a user with no rules does not refetch.
// Memory and storage can differ (another tab saved newer rules), so the newer entry wins.
export function getCachedRules(userId) {
  let winner = null;
  if (isFresh(memory, userId)) {
    winner = memory;
  }
  const stored = readStorage();
  if (isFresh(stored, userId)) {
    if (winner === null || stored.loadedAt > winner.loadedAt) {
      winner = stored;
    }
  }
  if (winner === null) {
    return null;
  }
  memory = winner;
  return winner.rules;
}

// Overwrites the cache (used after add / edit / toggle on the rules page).
export function setCachedRules(userId, rules) {
  memory = { userId, rules, loadedAt: Date.now() };
  writeStorage(memory);
}

export function clearDiscountRules() {
  memory = null;
  inFlight = null;
  try {
    localStorage.removeItem(DISCOUNT_RULES_STORAGE_KEY);
  } catch (err) {
    // Nothing to clear.
  }
  try {
    sessionStorage.removeItem(OLD_SESSION_KEY);
  } catch (err) {
    // Nothing to clear.
  }
}

// Cache first, otherwise one GET shared by every caller. A failure is not cached and gives [].
export function loadDiscountRules(userId) {
  const cached = getCachedRules(userId);
  if (cached) {
    return Promise.resolve(cached);
  }
  if (inFlight) {
    return inFlight;
  }
  const startedAt = Date.now();
  inFlight = api.get('/discount-rules')
    .then((res) => {
      if (!res.data || !res.data.success) {
        return [];
      }
      // A rule was saved while this GET was running: keep the newer list
      if (memory && memory.userId === userId && memory.loadedAt >= startedAt) {
        return memory.rules;
      }
      const rules = res.data.discountRules || [];
      setCachedRules(userId, rules);
      return rules;
    })
    .catch(() => [])
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Best active rule for the total: highest discount percent wins, a tie keeps the first.
export function pickBestRule(rules, total) {
  if (!rules || !(total > 0)) {
    return null;
  }
  let best = null;
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    if (String(rule.status).toUpperCase() !== 'ACTIVE') {
      continue;
    }
    if (total < Number(rule.minAmount)) {
      continue;
    }
    const hasMax = rule.maxAmount !== null && rule.maxAmount !== undefined && rule.maxAmount !== '';
    if (hasMax && total > Number(rule.maxAmount)) {
      continue;
    }
    if (best === null || Number(rule.discountPercent) > Number(best.discountPercent)) {
      best = rule;
    }
  }
  return best;
}
